'use client';

import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.js';
import { ModalProps } from '@sola/ark-core/molecules/modal/modal.js';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetBodyHeader,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useMakeAPaymentCommitments } from '@sola/supporter-sdk/hooks/commitments.js';

import { MakeAPaymentSection } from '../../molecules/make-a-payment-section/make-a-payment-section';

const getDefaultFormValues = (
  missedPayments: GraphQLCommitment[],
  amountDue: GraphQLCommitment[],
) => {
  return [...missedPayments, ...amountDue].reduce(
    (acc, commitment) => {
      acc[commitment?.internalId] =
        (commitment.dueThisMonthAmount || 0) +
        (commitment.missedPaymentAmount || 0);
      return acc;
    },
    {} as Record<string, number>,
  );
};

const MakeAPaymentSheetTotalAmount: React.FC<{
  totalAmount: number;
}> = ({ totalAmount }) => {
  const { t } = useTranslation('my');
  return (
    <SheetBodyHeader isSticky className="border flex flex-col gap-comfy-xl">
      <div className="flex flex-col gap-snug-lg">
        <span className="label-potent label-lg label-neutral-mild inline-block">
          {t('my:makeAPayment.total')}
        </span>
        <span className="label-xl label-potent label-neutral-amplified">
          {t('common:numbers.currencyCustom', {
            amount: totalAmount,
          })}
        </span>
      </div>

      <Divider purpose="neutral" />
    </SheetBodyHeader>
  );
};

export interface MakeAPaymentSheetProps {
  open?: ModalProps['open'];
  onOpenChange?: ModalProps['onOpenChange'];
}

export const MakeAPaymentSheet: React.FC<MakeAPaymentSheetProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation('my');

  const {
    amountDue,
    missedPaymentCommitments,
    amountDueCommitments,
    payAheadCommitments,
  } = useMakeAPaymentCommitments();

  const defaultFormValues = getDefaultFormValues(
    missedPaymentCommitments,
    amountDueCommitments,
  );

  const formMethods = useFormMethods();

  const [totalAmount, setTotalAmount] = useState(0);
  useEffect(() => {
    setTotalAmount(amountDue ?? 0);
  }, [amountDue]);

  const calculateTotalFromFormValues = (values: FieldValues): number => {
    return Object.values(values).reduce((sum, value) => {
      return (
        sum + (typeof value === 'number' && !Number.isNaN(value) ? value : 0)
      );
    }, 0);
  };

  // TODO: This will be moved to my-sdk
  const handleSubmit = (data: FieldValues) => {
    const validPayments = Object.entries(data)
      .filter(([_, amount]) => typeof amount === 'number' && amount > 0)
      .map(([commitmentId, amount]) => ({
        commitmentId,
        amount: Number(amount),
      }));

    if (validPayments.length === 0) {
      console.warn('No valid payments selected');
    }

    console.log('Submitting payment:', {
      commitments: validPayments,
      totalAmount: totalAmount.toFixed(2),
    });
    // TODO: Redirect to checkout with payment data
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent title={t('makeAPayment.title')}>
        <SheetHeader>{t('makeAPayment.title')}</SheetHeader>
        <SheetBody>
          <MakeAPaymentSheetTotalAmount totalAmount={totalAmount} />
          <Form
            className="mt-comfy-xl flex flex-col gap-comfy-xl"
            onChange={() => {
              setTotalAmount(
                calculateTotalFromFormValues(
                  formMethods.current?.getValues() || {},
                ),
              );
            }}
            onSubmit={handleSubmit}
            formMethods={formMethods}
            options={{
              mode: 'onTouched',
              defaultValues: defaultFormValues,
            }}
          >
            <MakeAPaymentSection
              title={t('makeAPayment.missingPayment.title')}
              description={t('makeAPayment.missingPayment.subtitle')}
              commitments={missedPaymentCommitments}
            />

            <MakeAPaymentSection
              title={t('makeAPayment.paymentDue.title')}
              description={t('makeAPayment.paymentDue.subtitle')}
              commitments={amountDueCommitments}
            />

            <MakeAPaymentSection
              title={t('makeAPayment.payAhead.title')}
              description={t('makeAPayment.payAhead.subtitle')}
              commitments={payAheadCommitments}
            />
          </Form>

          <SheetFooter>
            <SheetActions>
              <SheetClose asChild>
                <ButtonStyle
                  size="lg"
                  purpose="neutral"
                  variant="secondary"
                  fullWidth
                >
                  <button type="button">{t('core:actions.cancel')}</button>
                </ButtonStyle>
              </SheetClose>

              <ButtonStyle fullWidth>
                <button type="submit">{t('core:actions.continue')}</button>
              </ButtonStyle>
            </SheetActions>
          </SheetFooter>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};
